/*
 * Rhino-Watt Consent Banner
 * Nutzt die Shopify Customer Privacy API (setTrackingConsent) in der GRANULAREN
 * Objekt-Form, damit Google Consent Mode v2 saubere Signale erhaelt:
 *   analytics        -> analytics_storage
 *   marketing        -> ad_storage / ad_user_data / ad_personalization
 *   preferences      -> functionality_storage / personalization_storage
 *   sale_of_data     -> (US-Privacy, hier an marketing gekoppelt)
 * Equal-Weight: "Alle akzeptieren" und "Nur notwendige" sind beide 1-Klick,
 * gleich gross, gleich sichtbar. Granulare Auswahl ueber "Einstellungen".
 */
class CookiesBanner extends HTMLElement {
  constructor() {
    super();
    this.attachedEvents = false;
    this.initCookieBanner();
  }

  disconnectedCallback() {
    this.removeEvents();
  }

  initCookieBanner() {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return;

    const userCanBeTracked = cp.userCanBeTracked();
    const userTrackingConsent = cp.getTrackingConsent();

    if (!userCanBeTracked && userTrackingConsent === 'no_interaction') {
      this.showBanner();
    }
  }

  removeEvents() {
    if (!this._handlers) return;
    this._handlers.forEach(({ el, type, fn }) => {
      if (el) el.removeEventListener(type, fn);
    });
    this._handlers = null;
  }

  hideBanner(removeEvent = true) {
    this.classList.remove('open');
    this.setAttribute('aria-hidden', 'true');
    if (removeEvent && !Shopify.designMode) {
      this.removeEvents();
    }
  }

  openBanner() {
    this.classList.add('open');
    this.setAttribute('aria-hidden', 'false');
  }

  showBanner() {
    if (Shopify.designMode) {
      this.afterShowBanner();
    } else {
      fetch(window.Shopify.routes.root + '?section_id=cookies-banner&view=ajax')
        .then((response) => response.text())
        .then((responseText) => {
          const cookiesBannerInner = getDomHtmlFromText(responseText, '.cookies-banner__inner');
          if (cookiesBannerInner) {
            this.appendChild(cookiesBannerInner);
            this.afterShowBanner();
          }
        });
    }
  }

  afterShowBanner() {
    this.openBanner();
    if (this.attachedEvents) return;

    this._handlers = [];
    const bind = (el, type, fn) => {
      if (!el) return;
      el.addEventListener(type, fn);
      this._handlers.push({ el, type, fn });
    };

    const acceptBtns = this.querySelectorAll('.cookies-banner__button--accept, [data-action="accept-all"]');
    const declineBtn = this.querySelector('.cookies-banner__button--decline');
    const openSettings = this.querySelector('[data-action="open-settings"]');
    const closeSettings = this.querySelector('[data-action="close-settings"]');
    const saveBtn = this.querySelector('[data-action="save-selection"]');

    acceptBtns.forEach((btn) => bind(btn, 'click', () => this.acceptAll()));
    bind(declineBtn, 'click', () => this.declineAll());
    bind(openSettings, 'click', () => this.toggleSettings(true));
    bind(closeSettings, 'click', () => this.toggleSettings(false));
    bind(saveBtn, 'click', () => this.saveSelection());

    this.attachedEvents = true;
  }

  toggleSettings(show) {
    const main = this.querySelector('[data-view="main"]');
    const settings = this.querySelector('[data-view="settings"]');
    if (!main || !settings) return;
    main.hidden = show;
    settings.hidden = !show;
  }

  /* ---- Consent setzen ---- */

  consentObject(analytics, marketing) {
    // Shopify Customer Privacy API -> automatisches Mapping auf Consent Mode v2.
    // Die Boolean-Felder steuern die jeweiligen Storage-/Ad-Signale.
    return {
      analytics: !!analytics,
      marketing: !!marketing,
      preferences: !!analytics, // funktionale Praeferenzen an Analytics gekoppelt
      sale_of_data: !!marketing,
    };
  }

  applyConsent(consent, onDone) {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) {
      if (onDone) onDone();
      return;
    }
    cp.setTrackingConsent(consent, () => {
      if (onDone) onDone();
    });
  }

  acceptAll() {
    this.applyConsent(this.consentObject(true, true), this.hideBanner.bind(this));
  }

  declineAll() {
    this.applyConsent(this.consentObject(false, false), this.hideBanner.bind(this));
  }

  saveSelection() {
    const analytics = !!this.querySelector('[data-consent="analytics"]:checked');
    const marketing = !!this.querySelector('[data-consent="marketing"]:checked');
    this.applyConsent(this.consentObject(analytics, marketing), this.hideBanner.bind(this));
  }
}

window.Shopify.loadFeatures(
  [
    {
      name: 'consent-tracking-api',
      version: '0.1',
    },
  ],
  function (error) {
    if (error) {
      throw error;
    }
    customElements.define('cookies-banner', CookiesBanner);
  }
);
